/**
 * 🧠 AI 기억 보이기 — 이미 있는 기억(개인화) 기능을 조용히 알리는 규칙. 상태 없음.
 * 화면: 빈 화면 'AI가 기억하는 것 N개 · 관리' 한 줄, 답변 아래 '기억해 두었어요' 한 줄(조용한 자동 저장만), 설정의 개수·마지막 업데이트.
 * 기능이 꺼졌거나(feature_enabled=false) 선생님이 AI 맞춤 답변을 껐으면 아무것도 보이지 않는다.
 */

export const isMemoryVisibilityEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_MEMORY_VISIBILITY || '').trim().toLowerCase())
);

/** 개인화 스냅샷(GET /api/user/personalization)에서 기억을 보여도 되는지 */
export const isMemoryVisible = (snapshot) => Boolean(
  isMemoryVisibilityEnabled()
  && snapshot
  && snapshot.feature_enabled === true
  && snapshot.settings?.memory_enabled !== false
);

const isActiveMemory = (memory) => Boolean(memory) && memory.expired !== true;

export const memoryCountOf = (snapshot) => {
  if (!snapshot) return 0;
  const raw = Number(snapshot.active_memory_count ?? snapshot.memory_count);
  if (Number.isFinite(raw)) return Math.max(0, raw);
  return Array.isArray(snapshot.memories) ? snapshot.memories.filter(isActiveMemory).length : 0;
};

/** 빈 화면 한 줄: 기억이 1개 이상이고, 30초 설문 안내 카드가 없을 때만(카드 두 개를 겹쳐 보이지 않는다) */
export const shouldShowMemoryEntry = (snapshot, { surveyNudgeShown = false } = {}) => (
  !surveyNudgeShown && isMemoryVisible(snapshot) && memoryCountOf(snapshot) >= 1
);

/**
 * 답변 아래 한 줄: 이번 턴에 조용히(카드 없이) 저장된 경우만, 마지막 답변에만.
 * '기억해줘' 명시 저장은 실행취소가 있는 카드가 따로 있어 겹치지 않는다.
 */
export const shouldShowMemoryChip = (memoryUpdate, { isLatest = false, memoryVisible = false, readOnly = false } = {}) => Boolean(
  isLatest
  && !readOnly
  && memoryVisible
  && isMemoryVisibilityEnabled()
  && memoryUpdate
  && Number(memoryUpdate.saved_count || 0) > 0
  && !memoryUpdate.explicit
);

const toTime = (value) => {
  if (!value) return 0;
  if (typeof value === 'object') {
    const seconds = value._seconds ?? value.seconds;
    if (Number.isFinite(seconds)) return seconds * 1000;
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

/** 기억 목록에서 가장 최근에 바뀐 때(없으면 null) */
export const latestMemoryUpdate = (memories) => {
  const latest = (Array.isArray(memories) ? memories : [])
    .filter(isActiveMemory)
    .reduce((max, memory) => Math.max(max, toTime(memory.updated_at), toTime(memory.created_at)), 0);
  return latest > 0 ? new Date(latest) : null;
};

/** '10월 21일'(올해) · '2025년 10월 21일'(다른 해) */
export const formatMemoryDate = (date, now = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const monthDay = `${date.getMonth() + 1}월 ${date.getDate()}일`;
  return date.getFullYear() === now.getFullYear() ? monthDay : `${date.getFullYear()}년 ${monthDay}`;
};
